import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { useStore } from '@/contexts/StoreContext';
import { LayoutVehicleCard as VehicleCard } from '@/layouts';

interface FeaturedVehiclesProps {
  vehicles: any[];
  config: any;
  hidePlaceholders?: boolean;
  variant?: 'default' | 'wl3' | 'wl5' | 'wl6';
}

type PremiumVariant = 'wl3' | 'wl5' | 'wl6';

const VehiclePlaceholder = ({ variant }: { variant: PremiumVariant }) => {
  const wl = variant;
  if (variant === 'wl3' || variant === 'wl5' || variant === 'wl6') {
    return (
      <div className={`${wl}-vehicle-placeholder`} aria-hidden="true">
        <div className={`${wl}-vehicle-placeholder-media`}>
          <Star className={`h-9 w-9 text-[var(--${wl}-muted)] opacity-35`} />
        </div>
        <div className={`${wl}-vehicle-placeholder-body`}>
          <div className={`${wl}-vehicle-placeholder-line w-3/4`} />
          <div className={`flex gap-2 pt-1`}>
            <div className={`${wl}-vehicle-placeholder-line w-16 h-2`} />
            <div className={`${wl}-vehicle-placeholder-line w-14 h-2`} />
            <div className={`${wl}-vehicle-placeholder-line w-12 h-2`} />
          </div>
          <div className={`flex items-center justify-between pt-3 mt-auto border-t border-[var(--${wl}-border)]`}>
            <div className={`${wl}-vehicle-placeholder-line w-24 h-5`} />
            <div className={`${wl}-vehicle-placeholder-line w-20 h-7 rounded-lg`} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <Card className="overflow-hidden border-dashed border-border/50 bg-muted/20 flex flex-col h-full">
      <div className="relative aspect-[4/2.5] overflow-hidden flex-shrink-0 bg-muted/30 flex items-center justify-center">
        <Star className="h-10 w-10 text-muted-foreground/30" />
      </div>
      <CardContent className="p-4 flex flex-col">
        <div className="mb-3 flex-shrink-0">
          <div className="h-6 bg-muted/50 rounded w-3/4 mb-2" />
          <div className="h-4 bg-muted/30 rounded w-1/2 mb-2" />
          <div className="h-7 bg-muted/50 rounded w-2/3" />
        </div>
        <div className="flex items-center gap-2 border-t border-border/50 pt-3 flex-shrink-0 h-8">
          <div className="h-3 bg-muted/30 rounded w-16" />
          <div className="w-1 h-1 bg-muted/30 rounded-full" />
          <div className="h-3 bg-muted/30 rounded w-12" />
        </div>
        <div className="h-4 mt-3 flex-shrink-0" />
      </CardContent>
    </Card>
  );
};

export const FeaturedVehicles = ({ vehicles, config, hidePlaceholders = false, variant = 'default' }: FeaturedVehiclesProps) => {
  const { colors } = useTheme();
  const { selectedStore } = useStore();

  const buttonColor = colors.button;
  const titleColor = colors.title;

  // Filter featured vehicles (those with high views/favorites or marked as featured)
  const featuredVehicles = useMemo(() => {
    return vehicles.filter(vehicle =>
      vehicle.changed?.featured ||
      (vehicle.statistics?.views && vehicle.statistics.views > 100) ||
      (vehicle.statistics?.favorites && vehicle.statistics.favorites > 3)
    );
  }, [vehicles]);

  const store = selectedStore || config.stores[0];
  const isPremium = variant === 'wl3' || variant === 'wl5' || variant === 'wl6';
  const wl = variant === 'wl5' ? 'wl5' : variant === 'wl6' ? 'wl6' : 'wl3';
  const slotWidth = isPremium ? '' : 'flex-shrink-0 w-[280px] md:w-[330px] snap-start';
  const displayVehicles = featuredVehicles.slice(0, 10);
  const targetSlots = isPremium ? 4 : 5;
  const placeholdersNeeded = !hidePlaceholders && displayVehicles.length < targetSlots
    ? targetSlots - displayVehicles.length
    : 0;
  const totalItems = displayVehicles.length + placeholdersNeeded;
  const useGrid = isPremium && totalItems <= 4;

  if (featuredVehicles.length === 0) return null;

  const vehicleItems = displayVehicles.map((vehicle) => (
    <div key={vehicle.id} className={useGrid ? 'min-w-0' : `${slotWidth} flex-shrink-0 snap-start`}>
      <VehicleCard vehicle={vehicle} whatsappUrl={store.whatsapp?.ios} />
    </div>
  ));

  const placeholderItems = placeholdersNeeded > 0
    ? Array.from({ length: placeholdersNeeded }).map((_, index) => (
      <div key={`placeholder-${index}`} className={useGrid ? 'min-w-0' : `${slotWidth} flex-shrink-0 snap-start`}>
        <VehiclePlaceholder variant={variant as PremiumVariant} />
      </div>
    ))
    : null;

  return (
    <section
      id="ofertas"
      className={
        variant === 'wl5'
          ? 'wl5-home-featured'
          : isPremium
            ? ''
            : 'bg-muted/30 overflow-hidden'
      }
    >
      <div className={isPremium ? `${wl}-container` : 'container mx-auto px-4'}>
        {variant === 'wl5' ? (
          <div className="wl5-home-section-head">
            <div>
              <p className="wl5-eyebrow">Destaques</p>
              <h2 className="wl5-home-section-title" style={{ color: titleColor }}>
                {store.featuredTitle || 'Ofertas'}
              </h2>
            </div>
            <Link to="/estoque" className="wl5-home-section-link" style={{ color: buttonColor }}>
              Ver estoque →
            </Link>
          </div>
        ) : isPremium ? (
          <div className={`${wl}-section-header !mb-5`}>
            <h2 className={`${wl}-h2`}>{store.featuredTitle || 'Ofertas'}</h2>
          </div>
        ) : (
          <div className="mb-4">
            <h2 className="text-2xl font-bold whitespace-nowrap">
              {store.featuredTitle || 'Destaques'}
            </h2>
          </div>
        )}

        {useGrid ? (
          <div className={`${wl}-offers-grid`}>
            {vehicleItems}
            {placeholderItems}
          </div>
        ) : (
        <div className="relative group/carousel">
          <div
            id="featured-scroll"
            className={`flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide scroll-smooth pb-4 ${isPremium ? `${wl}-offers-scroll` : ''}`}
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
          >
              {vehicleItems}
              {placeholderItems}
            </div>

            {displayVehicles.length > 5 && (
              <>
                <button
                  className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 bg-white/95 hover:bg-white rounded-full p-2.5 transition-all opacity-0 group-hover/carousel:opacity-100 z-10 border border-gray-200"
                  onClick={() => {
                    const container = document.getElementById('featured-scroll');
                    if (container) {
                      const isMobile = window.innerWidth < 768;
                      const scrollAmount = isMobile ? 296 : 316; // 280px/300px + 16px gap
                      container.scrollBy({
                        left: -scrollAmount,
                        behavior: 'smooth'
                      });
                    }
                  }}
                  aria-label="Anterior"
                >
                  <ChevronLeft className="h-5 w-5 text-gray-800" />
                </button>

                <button
                  className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 bg-white/95 hover:bg-white rounded-full p-2.5 transition-all opacity-0 group-hover/carousel:opacity-100 z-10 border border-gray-200"
                  onClick={() => {
                    const container = document.getElementById('featured-scroll');
                    if (container) {
                      const isMobile = window.innerWidth < 768;
                      const scrollAmount = isMobile ? 296 : 316; // 280px/300px + 16px gap
                      container.scrollBy({
                        left: scrollAmount,
                        behavior: 'smooth'
                      });
                    }
                  }}
                  aria-label="Próximo"
                >
                  <ChevronRight className="h-5 w-5 text-gray-800" />
                </button>
              </>
            )}
        </div>
        )}
      </div>
    </section>
  );
};